import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import type { ForecastScore, DayInputs } from "../types/forecast";
import { fetchEnhancedWeather } from "../lib/enhancedWeather";
import { buildUnavailableForecast, forecastForDay } from "../lib/forecast";
import {
  buildForecastCacheKey,
  getOfflineForecastCache,
  isCacheStale,
  saveOfflineForecastCache,
} from "../lib/offlineForecastCache";
import {
  addDaysToDate,
  validateNorthAmericaCoords,
  getAstronomicalTimes,
  getSolunarTimes,
  getTimezoneFromCoords,
} from "../lib/time";
import ScoreCard from "../components/ScoreCard";
import NWSOfficeInfo from "../components/NWSOfficeInfo";
import Icon from "../components/Icon";

export default function Results() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [forecasts, setForecasts] = useState<ForecastScore[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useFahrenheit, setUseFahrenheit] = useState(true);
  const [useMph, setUseMph] = useState(true);
  const [isOffline, setIsOffline] = useState(
    typeof navigator !== "undefined" ? !navigator.onLine : false
  );
  const [usedCachedData, setUsedCachedData] = useState(false);
  const [cachedLastUpdatedIso, setCachedLastUpdatedIso] = useState<
    string | null
  >(null);
  const [isCachedDataStale, setIsCachedDataStale] = useState(false);

  // Parse URL parameters
  const lat = parseFloat(searchParams.get("lat") || "0");
  const lon = parseFloat(searchParams.get("lon") || "0");
  const startDate = searchParams.get("startDate") || "";
  const days = parseInt(searchParams.get("days") || "0", 10);
  const locationName = searchParams.get("name") || "";
  const cacheKey = buildForecastCacheKey({
    lat,
    lon,
    startDate,
    days,
  });

  const formatCacheTimestamp = (iso: string | null) => {
    if (!iso) {
      return "Unknown";
    }

    const timestamp = new Date(iso);
    if (Number.isNaN(timestamp.getTime())) {
      return "Unknown";
    }

    const diffMs = Date.now() - timestamp.getTime();
    const minutes = Math.max(0, Math.floor(diffMs / 60000));
    const relative =
      minutes < 1
        ? "just now"
        : minutes < 60
        ? `${minutes} min ago`
        : `${Math.floor(minutes / 60)} hr ago`;

    return `${relative} @ ${timestamp.toLocaleString()}`;
  };

  const generateForecasts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setUsedCachedData(false);
    setCachedLastUpdatedIso(null);
    setIsCachedDataStale(false);

    try {
      const results: ForecastScore[] = [];

      // Generate forecasts for each day
      for (let i = 0; i < days; i++) {
        const currentDate = addDaysToDate(startDate, i);
        const dayInputs: DayInputs = {
          date: currentDate,
          lat,
          lon,
        };

        // Fetch enhanced weather data; UNAVAILABLE blocks the bite score
        const weatherResult = await fetchEnhancedWeather(dayInputs);

        if (weatherResult.status === "UNAVAILABLE") {
          results.push(buildUnavailableForecast(dayInputs, weatherResult.reason));
          continue;
        }

        // Generate forecast (no almanac data for now)
        const forecast = forecastForDay(dayInputs, weatherResult.weather);

        // Add astronomical and solunar data
        const timezone = getTimezoneFromCoords(lat, lon);
        const astronomical = getAstronomicalTimes(
          currentDate,
          lat,
          lon,
          timezone
        );
        const solunar = getSolunarTimes(
          currentDate,
          lat,
          lon,
          forecast.moon.illumination,
          timezone
        );

        forecast.astronomical = astronomical;
        forecast.solunar = solunar;

        results.push(forecast);
      }

      const allUnavailable = results.every(
        (f) => f.forecastStatus === "WEATHER_UNAVAILABLE"
      );

      if (allUnavailable) {
        // Prefer a cached real forecast (clearly marked cached/stale) over a
        // page of blocked cards, and never overwrite the cache with them.
        const cached = getOfflineForecastCache(cacheKey);
        if (cached && cached.forecasts.length > 0) {
          setForecasts(cached.forecasts);
          setUsedCachedData(true);
          setCachedLastUpdatedIso(cached.lastUpdatedIso);
          setIsCachedDataStale(isCacheStale(cached.lastUpdatedIso));
          return;
        }
        setForecasts(results);
        return;
      }

      setForecasts(results);
      saveOfflineForecastCache({
        cacheKey,
        lat,
        lon,
        startDate,
        days,
        locationName: locationName || undefined,
        forecasts: results,
      });
    } catch (err) {
      const cached = getOfflineForecastCache(cacheKey);
      if (cached && cached.forecasts.length > 0) {
        setForecasts(cached.forecasts);
        setUsedCachedData(true);
        setCachedLastUpdatedIso(cached.lastUpdatedIso);
        setIsCachedDataStale(isCacheStale(cached.lastUpdatedIso));
        setError(null);
      } else {
        const baseMessage =
          err instanceof Error ? err.message : "Failed to generate forecast";
        const offlineMessage = !navigator.onLine
          ? `${baseMessage}. You are offline and no cached forecast is available yet.`
          : baseMessage;
        setError(offlineMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [cacheKey, days, lat, locationName, lon, startDate]);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    // Validate parameters
    if (!lat || !lon || !startDate || !days) {
      setError("Missing required parameters");
      setIsLoading(false);
      return;
    }

    if (!validateNorthAmericaCoords(lat, lon)) {
      setError("Location is outside North America");
      setIsLoading(false);
      return;
    }

    if (days < 1 || days > 7) {
      setError("Days must be between 1 and 7");
      setIsLoading(false);
      return;
    }

    generateForecasts();
  }, [lat, lon, startDate, days, generateForecasts]);

  const handleBackToHome = () => {
    navigate("/");
  };

  const availableForecasts = forecasts.filter(
    (f) => f.forecastStatus !== "WEATHER_UNAVAILABLE"
  );
  const unavailableCount = forecasts.length - availableForecasts.length;

  const averageScore =
    availableForecasts.length > 0
      ? Math.round(
          (availableForecasts.reduce((sum, f) => sum + f.biteScore0100, 0) /
            availableForecasts.length) *
            10
        ) / 10
      : 0;

  const bestDay = availableForecasts.reduce(
    (best, current) =>
      current.biteScore0100 > best.biteScore0100 ? current : best,
    availableForecasts[0]
  );

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <div
            className="spinner mx-auto mb-4"
            style={{ width: "40px", height: "40px" }}
          ></div>
          <h2 className="text-xl font-semibold mb-2">Generating Forecast...</h2>
          <p className="text-gray-600">
            Fetching weather data and calculating bite scores
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="card p-6 text-center">
          <div className="text-4xl mb-4">
            <Icon name="warning" className="text-4xl" />
          </div>
          <h2 className="text-xl font-semibold mb-2 text-red-600">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button className="btn btn-primary" onClick={handleBackToHome}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex mb-6 results-header" id="results-header">
        <div className="results-header-title" id="results-header-title">
          <h1 className="text-2xl font-bold text-gray-900">Fishing Forecast</h1>
          <p className="text-gray-600">
            {locationName || `${lat.toFixed(4)}, ${lon.toFixed(4)}`}
          </p>
        </div>

        <div
          className="flex gap-4 results-header-controls"
          id="results-header-controls"
        >
          {/* Unit toggles */}
          <div
            className="flex items-center gap-2 results-unit-group"
            id="temperature-unit-group"
          >
            <button
              className={`btn ${
                !useFahrenheit ? "btn-primary" : "btn-secondary"
              }`}
              id="unit-toggle-celsius"
              onClick={() => setUseFahrenheit(false)}
            >
              °C
            </button>
            <button
              className={`btn ${
                useFahrenheit ? "btn-primary" : "btn-secondary"
              }`}
              id="unit-toggle-fahrenheit"
              onClick={() => setUseFahrenheit(true)}
            >
              °F
            </button>
          </div>

          <div
            className="flex items-center gap-2 results-unit-group"
            id="wind-unit-group"
          >
            <button
              className={`btn ${!useMph ? "btn-primary" : "btn-secondary"}`}
              id="unit-toggle-kmh"
              onClick={() => setUseMph(false)}
            >
              km/h
            </button>
            <button
              className={`btn ${useMph ? "btn-primary" : "btn-secondary"}`}
              id="unit-toggle-mph"
              onClick={() => setUseMph(true)}
            >
              mph
            </button>
          </div>

          <button
            className="btn btn-primary"
            id="action-new-forecast"
            onClick={handleBackToHome}
          >
            New Forecast
          </button>
        </div>
      </div>

      {(isOffline || usedCachedData) && (
        <div className="card p-4 mb-6" id="offline-status-banner">
          <p className="text-sm font-semibold text-primary">
            <Icon name="warning" className="mr-2" />
            {isOffline ? "Offline mode active" : "Using cached forecast data"}
          </p>
          {usedCachedData && (
            <p className="text-xs text-secondary mt-1">
              Last updated: {formatCacheTimestamp(cachedLastUpdatedIso)}
              {isCachedDataStale ? " (stale)" : ""}
            </p>
          )}
        </div>
      )}

      {unavailableCount > 0 && (
        <div
          className="card p-4 mb-6 border-2 border-red-200 bg-red-50"
          id="weather-unavailable-banner"
        >
          <p className="text-sm font-semibold text-red-700">
            <Icon name="warning" className="mr-2" />
            {unavailableCount === forecasts.length
              ? "Weather unavailable. Bite scores paused."
              : `Weather unavailable for ${unavailableCount} of ${forecasts.length} days. Those bite scores are paused.`}
          </p>
          <p className="text-xs text-red-700 mt-1">
            Current weather could not be verified. Check official weather
            before fishing.
          </p>
        </div>
      )}

      {/* Summary */}
      {availableForecasts.length > 0 && (
        <div className="grid gap-6 sm:grid-cols-2 mb-8" id="results-summary">
          <div className="card p-6" id="overall-outlook-card">
            <h3
              className="text-lg font-semibold mb-2"
              id="overall-outlook-title"
            >
              Overall Outlook
            </h3>
            <div
              className="text-3xl font-bold text-blue-600 mb-2"
              id="overall-outlook-score"
            >
              {averageScore}
            </div>
            <p className="text-sm text-gray-600" id="overall-outlook-label">
              Average bite score
            </p>
          </div>

          <div className="card p-6" id="best-day-card">
            <h3 className="text-lg font-semibold mb-2" id="best-day-title">
              Best Day
            </h3>
            <div className="text-lg font-medium mb-1" id="best-day-date">
              {new Date(bestDay.date + "T12:00:00").toLocaleDateString(
                "en-US",
                {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                }
              )}
            </div>
            <div
              className="text-2xl font-bold text-green-600"
              id="best-day-score"
            >
              {bestDay.biteScore0100}
            </div>
          </div>
        </div>
      )}

      {/* NWS Office Information */}
      {forecasts.length > 0 &&
        forecasts[0]?.weather?.source === "NWS" &&
        forecasts[0]?.weather?.localOffice && (
          <div className="mb-8" id="nws-office-section">
            <NWSOfficeInfo
              localOffice={forecasts[0].weather.localOffice}
              className="max-w-none"
              id="nws-office-card"
            />
          </div>
        )}

      {/* Daily Forecasts */}
      <div className="grid gap-6 lg:grid-cols-2">
        {forecasts.map((forecast) => (
          <ScoreCard
            key={forecast.date}
            forecast={forecast}
            lat={lat}
            lon={lon}
            useFahrenheit={useFahrenheit}
            useMph={useMph}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-sm text-gray-500">
        <p>
          Data sources: NWS grid/alerts, SPC convective outlooks, NOAA tides &
          marine, Open-Meteo fallback, astronomical calculations for lunar
          phase
        </p>
        <p className="mt-1">
          Forecasts are for entertainment purposes and should not be the sole
          factor in fishing decisions
        </p>
      </div>
    </div>
  );
}
